import CountryAutocomplete from "components/CountryAutocomplete";
import NumberFormComponent from "components/shared/FormComponent/NumberFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import StyledButton from "components/shared/StyledButton";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ICompany } from "store/settingsStore";
import { patchCompanyInfo } from "store/settingsStore";
import { useAppDispatch } from "store/store";

import styles from "../style.module.scss";

interface ICompanyForm {
    company: ICompany | undefined;
    handleClose: () => void;
}

type TCompanyFormValues = {
    address: string;
    postalCode: string;
    city: string;
    country: { name: string; flag: string };
    phoneNumber: string;
    activityType: string;
    apeCode: string;
};

/**
 * Within DAS2 page, modal to complete company infos if missing.
 */
const CompanyForm = ({ company, handleClose }: ICompanyForm) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const [submitPending, setSubmitPending] = useState(false);

    const defaultValues: TCompanyFormValues = {
        address: company?.address ?? "",
        postalCode: company?.postalCode ?? "",
        city: company?.city ?? "",
        country: { name: company?.country ?? "", flag: "" },
        phoneNumber: company?.phoneNumber ?? "",
        activityType: company?.activityType ?? "",
        apeCode: company?.apeCode ?? "",
    };

    const {
        control,
        formState: { errors },
        setError,
        handleSubmit,
    } = useForm({ defaultValues, mode: "all" });

    // Sets up errors for fields that are empty on modal load.
    useEffect(() => {
        if (company) {
            Object.keys(company).forEach((item) => {
                if (item in defaultValues && company[item] === "") {
                    setError(item as keyof TCompanyFormValues, { message: t("Declarations.DAS2.InfoError") });
                }
            });
        }
    }, [company]);

    const onSubmit = async (data: TCompanyFormValues) => {
        setSubmitPending(true);
        const req = {
            ...company,
            ...data,
            country: data.country.name,
            phoneNumber: data.phoneNumber.toString(),
        };

        const response = await dispatch(patchCompanyInfo(req));
        if (response.meta.requestStatus === "fulfilled") {
            setSubmitPending(false);
            handleClose();
        } else {
            if (response.payload.message === "ServerError.InvalidCompanyBankInfos") {
                handleClose();
            }
            setSubmitPending(false);
        }
    };

    return (
        <form className={styles["company-form-container"]} onSubmit={handleSubmit(onSubmit)}>
            <TextFormComponent
                control={control}
                controlName="address"
                label={t("BeneficiarInfo.StreetNumber")}
                error={errors.address}
                required
            />
            <TextFormComponent
                control={control}
                controlName="postalCode"
                label={t("BeneficiarInfo.ZIPCode")}
                error={errors.postalCode}
                required
            />
            <TextFormComponent
                control={control}
                controlName="city"
                label={t("BeneficiarInfo.City")}
                error={errors.city}
                required
            />
            <CountryAutocomplete control={control} errors={errors} required />
            <NumberFormComponent
                label={t("CompanySettings.PhoneNumber")}
                control={control}
                controlName="phoneNumber"
                error={errors.phoneNumber}
                required
            />
            <TextFormComponent
                control={control}
                controlName="activityType"
                label={t("CompanySettings.ActivityType")}
                error={errors.activityType}
                required
            />
            <TextFormComponent
                control={control}
                controlName="apeCode"
                label={t("CompanySettings.APECode")}
                error={errors.apeCode}
                required
            />
            <StyledButton buttonType="primary" type="submit" loading={submitPending}>
                {t("Declarations.DAS2.SaveInfo")}
            </StyledButton>
        </form>
    );
};

export default CompanyForm;
